import React from 'react';
import { Link, useLocation } from 'react-router-dom';

const ConsultantStatusPage = () => {
  const location = useLocation();
  const status = location.state?.consultant_status || 'pending';
  const email = location.state?.email || '';

  const getStatusConfig = (status) => {
    switch (status) {
      case 'pending':
        return {
          title: 'Account Under Review',
          message: 'Your consultant account is currently being reviewed by our admin team.',
          description: 'We review all consultant applications to ensure quality and expertise. This process typically takes 1-2 business days.',
          icon: '⏳',
          color: 'bg-yellow-50 border-yellow-200',
          textColor: 'text-yellow-800',
          iconColor: 'text-yellow-600'
        };
      case 'rejected':
        return {
          title: 'Application Not Approved',
          message: 'Your consultant account application has been declined.',
          description: 'If you believe this is an error or would like to reapply, please contact our support team for assistance.',
          icon: '❌',
          color: 'bg-red-50 border-red-200',
          textColor: 'text-red-800',
          iconColor: 'text-red-600'
        };
      default:
        return {
          title: 'Account Status Update',
          message: 'Your consultant account status has been updated.',
          description: 'Please contact support if you have any questions about your account status.',
          icon: 'ℹ️',
          color: 'bg-blue-50 border-blue-200',
          textColor: 'text-blue-800',
          iconColor: 'text-blue-600'
        };
    }
  };

  const config = getStatusConfig(status);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div>
          <div className="flex justify-center">
            <Link to="/" className="flex items-center">
              <div className="w-12 h-12 bg-emerald-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">RA</span>
              </div>
            </Link>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Consultant Application
          </h2>
        </div>

        {/* Status Card */}
        <div className={`${config.color} border rounded-lg p-6`}>
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <span className={`text-2xl ${config.iconColor}`}>
                {config.icon}
              </span>
            </div>
            <div className="ml-4">
              <h3 className={`text-lg font-medium ${config.textColor}`}>
                {config.title}
              </h3>
              <p className={`mt-1 text-sm ${config.textColor}`}>
                {config.message}
              </p>
            </div>
          </div>
          
          <div className="mt-4">
            <p className={`text-sm ${config.textColor}`}>
              {config.description}
            </p>
            
            {email && (
              <p className="mt-2 text-xs text-gray-600">
                Account: {email}
              </p>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-4">
          {status === 'pending' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-700">
                <strong>What happens next?</strong><br/>
                • Our team will review your qualifications<br/>
                • You'll receive an email notification when approved<br/>
                • Approved consultants can access the full consultant portal
              </p>
            </div>
          )}
          
          {status === 'rejected' && (
            <div className="space-y-3">
              <Link 
                to="mailto:support@relyability.ai?subject=Consultant Application Review"
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
              >
                Contact Support
              </Link>
              <Link 
                to="/register"
                className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500"
              >
                Apply Again
              </Link>
            </div>
          )}
          
          <div className="text-center">
            <Link to="/" className="text-emerald-600 hover:text-emerald-700 text-sm">
              ← Back to Homepage
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultantStatusPage;